import { useEffect, useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { Credential } from '@/types';

export function useLegacyAwsAccounts(
  customerEmail?: string,
  enabled = true
): {
  isLoaded: boolean;
  isLegacyAwsAccount: (accountKey: string) => boolean;
} {
  const {
    data: awsCredentials = [],
    status,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useMy(
    {
      resource: 'credential',
      query: {
        node: {
          labels: ['Credential'],
          filters: [
            { field: 'category', operator: '=', value: 'env-integration' },
            { field: 'type', operator: '=', value: 'aws' },
          ],
        },
      },
    },
    { email: customerEmail, enabled }
  );

  useEffect(() => {
    // useMy sets retry:false, and TanStack Query's `status` reflects only the
    // FIRST page. A failed subsequent fetchNextPage() leaves status 'success'
    // with hasNextPage still true and isFetchingNextPage back to false — which
    // would re-fire fetchNextPage() in a tight unthrottled loop. Guard on
    // isFetchNextPageError (from the underlying useInfiniteQuery) to stop it.
    if (status === 'error' || isFetchNextPageError) return;
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    status,
    isFetchNextPageError,
  ]);

  const accountsWithCredential = useMemo(
    () =>
      new Set((awsCredentials as Credential[]).map(cred => cred.accountKey)),
    [awsCredentials]
  );

  const isLoaded = status === 'success' && !hasNextPage;

  return {
    isLoaded,
    isLegacyAwsAccount: (accountKey: string) =>
      isLoaded && !accountsWithCredential.has(accountKey),
  };
}
